import { useState, useEffect, useRef, useCallback } from 'react'
import { X, Save, Share2 } from 'lucide-react'
import ShareModal from './ShareModal'
import { useSwipeDismiss } from '../hooks/useSwipeDismiss'
import { useT, useLang } from '../i18n/useT'
import ManualForm from './quickadd/ManualForm'
import VoiceMode from './quickadd/VoiceMode'
import ImageMode from './quickadd/ImageMode'
import { stripPastDate } from './quickadd/helpers'
import { suppressBackToast } from '../lib/backGuardSignals'

export default function QuickAddModal({ mode, initialData, onClose, onSave, onToggle }) {
  const t = useT()
  const lang = useLang()
  const [currentMode, setCurrentMode] = useState(mode)
  const [currentData, setCurrentData] = useState(initialData)

  // Close modal on browser back button (mobile)
  const historyPushedRef = useRef(false)
  useEffect(() => {
    // Delay pushState to avoid StrictMode double-mount issues
    const timer = setTimeout(() => {
      history.pushState({ modal: true }, '')
      historyPushedRef.current = true
    }, 50)

    const handleBack = () => {
      historyPushedRef.current = false
      onClose()
    }
    window.addEventListener('popstate', handleBack)
    return () => {
      clearTimeout(timer)
      window.removeEventListener('popstate', handleBack)
      if (historyPushedRef.current) {
        historyPushedRef.current = false
        // Pop "silenzioso" per rimuovere l'entry che avevamo spinto
        // all'apertura. Segnala a BackGuard di non trattarlo come
        // exit-attempt (→ niente toast "premi ancora per uscire").
        suppressBackToast(500)
        history.back()
      }
    }
  }, []) // eslint-disable-line

  const isEditing = !!initialData?.id
  const [showShare, setShowShare] = useState(false)
  const animateClose = useCallback(() => {
    if (sheetRef.current) {
      sheetRef.current.style.transition = 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)'
      sheetRef.current.style.transform = 'translateY(100%)'
      setTimeout(() => onClose(), 350)
    } else {
      onClose()
    }
  }, [onClose])

  const { sheetRef, onTouchStart, onTouchMove, onTouchEnd } = useSwipeDismiss(animateClose)

  const titles = {
    manual: isEditing ? t('modal_edit') : t('modal_manual'),
    voice:  t('modal_voice'),
    image:  t('modal_image'),
  }

  const handleEditResult = (parsedData) => {
    setCurrentData(stripPastDate(parsedData, lang, t))
    setCurrentMode('manual')
  }

  // Intercept back/close: let child modes handle it first
  const childBackHandler = useRef(null)
  const handleClose = () => {
    if (childBackHandler.current && childBackHandler.current()) return
    animateClose()
  }

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) handleClose() }}>
      <div className="modal-sheet" ref={sheetRef} onClick={e => e.stopPropagation()}
        onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
        <div className="modal-handle" />
        <div className="modal-header">
          <span className="modal-title">{titles[currentMode] || titles.manual}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isEditing && currentMode === 'manual' && initialData.dueDate && (
              <button type="button" className="btn-icon modal-save-btn" title={t('share_title')} onClick={() => setShowShare(true)}>
                <Share2 size={18} />
              </button>
            )}
            {isEditing && currentMode === 'manual' && (
              <button type="submit" form="quickadd-form" className="btn-icon modal-save-btn" title={t('modal_save_edit')}>
                <Save size={18} />
              </button>
            )}
            <button className="btn-icon" onClick={handleClose} aria-label={t('voice_close')}>
              <X size={20} />
            </button>
          </div>

          {showShare && <ShareModal task={initialData} onClose={() => setShowShare(false)} />}
        </div>

        {currentMode === 'manual' && (
          <ManualForm initialData={currentData} onSave={onSave} onClose={onClose} onToggle={isEditing ? onToggle : null} />
        )}
        {currentMode === 'voice' && (
          <VoiceMode onSave={onSave} onClose={onClose} onEditResult={handleEditResult} />
        )}
        {currentMode === 'image' && (
          <ImageMode onSave={onSave} onClose={onClose} backHandlerRef={childBackHandler} />
        )}
      </div>
    </div>
  )
}
